import type {
  BindableQrTargetType,
  QrBindingByTargetResponseDto,
  QrTargetType,
} from '@prospero/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { QrBindingService } from '../../services/qr-binding/qr-binding.service';
import type { IQrBindingService } from '../../services/qr-binding/qr-binding.service.interface';
import { type QrBindingsBatchState, useCoveringQrBindingsBatch } from './qr-bindings-batch-context';
import { qrBindingQueryKeys } from './query-keys';

const QR_BINDING_STALE_TIME_MS = 5 * 60 * 1000;

export interface QrBindingLookup {
  data?: QrBindingByTargetResponseDto;
  /** True while the binding is unknown: never render it as "not bound" in that state. */
  isPending: boolean;
  /** True when the binding is unknown because its request failed, not because it is loading. */
  isError: boolean;
}

export function useQrBindingForTarget(
  targetType: QrTargetType,
  targetId: string,
  enabled = true,
): QrBindingLookup {
  const service = useInjection<IQrBindingService>(QrBindingService);
  const coveringBatch = useCoveringQrBindingsBatch(targetType, targetId);
  const ownQuery = useQuery({
    queryKey: qrBindingQueryKeys.byTarget(targetType, targetId),
    queryFn: () => service.getByTarget(targetType, targetId),
    enabled: enabled && Boolean(targetId) && !coveringBatch,
    staleTime: QR_BINDING_STALE_TIME_MS,
  });

  if (coveringBatch) {
    return readBindingFromBatch(coveringBatch, targetId);
  }
  return { data: ownQuery.data, isPending: ownQuery.isPending, isError: ownQuery.isError };
}

function readBindingFromBatch(batchState: QrBindingsBatchState, targetId: string): QrBindingLookup {
  const bindingsByTargetId = batchState.bindingsByTargetId;
  if (!bindingsByTargetId?.has(targetId)) {
    return { data: undefined, isPending: true, isError: batchState.isError };
  }
  return {
    data: { qrUuid: bindingsByTargetId.get(targetId) ?? null },
    isPending: false,
    isError: false,
  };
}

/** One request for the bindings of a whole list (see `QrBindingsPrefetch`). */
export function useQrBindingsForTargets(
  targetType: BindableQrTargetType,
  targetIds: readonly string[],
) {
  const service = useInjection<IQrBindingService>(QrBindingService);
  const sortedTargetIds = [...new Set(targetIds)].sort();
  return useQuery({
    queryKey: qrBindingQueryKeys.byTargets(targetType, sortedTargetIds),
    queryFn: () => service.getByTargets(targetType, sortedTargetIds),
    enabled: sortedTargetIds.length > 0,
    staleTime: QR_BINDING_STALE_TIME_MS,
    // A changed list (e.g. another day) keeps the known bindings while the new batch loads.
    placeholderData: keepPreviousData,
  });
}

export function useResolveQrPreviewQuery(qrUuid: string | null) {
  const service = useInjection<IQrBindingService>(QrBindingService);
  return useQuery({
    queryKey: qrBindingQueryKeys.resolve(qrUuid ?? ''),
    queryFn: ({ signal }) => service.resolve(qrUuid as string, signal),
    enabled: !!qrUuid,
    staleTime: 30_000,
    retry: false,
  });
}
